import { Component, signal, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PwaService } from '../../../core/services/pwa.service';

/**
 * PWA Install Prompt Component
 * Displays a dismissible banner prompting users to install the PWA
 * Only shown when:
 * - App is not already installed
 * - Browser supports installation
 * - User hasn't dismissed the prompt
 */
@Component({
    selector: 'app-pwa-install-prompt',
    standalone: true,
    imports: [CommonModule],
    template: `
    @if (showPrompt()) {
      <div class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div class="flex-shrink-0">
              <svg
                class="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                ></path>
              </svg>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                Install VEO Dashboard
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Install our app for faster access and offline support
              </p>

              <!-- Actions -->
              <div class="flex gap-2">
                <button
                  (click)="install()"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  Install
                </button>
                <button
                  (click)="dismiss()"
                  class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>

            <!-- Close button -->
            <button
              (click)="dismiss()"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    }
  `,
    styles: [
        `
      @keyframes slide-up {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .animate-slide-up {
        animation: slide-up 0.3s ease-out;
      }
    `,
    ],
})
export class PwaInstallPromptComponent {
    private pwaService = inject(PwaService);

    showPrompt = signal(false);

    constructor() {
        // Check if we should show the prompt
        effect(
            () => {
                const dismissed = localStorage.getItem('pwa-install-dismissed');
                const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
                const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

                // Show if not dismissed or dismissed more than 24 hours ago
                const shouldShow =
                    !this.pwaService.isInstalled() &&
                    this.pwaService.canInstall() &&
                    dismissedTime < oneDayAgo;

                this.showPrompt.set(shouldShow);
            },
            { allowSignalWrites: true },
        );
    }

    async install(): Promise<void> {
        const installed = await this.pwaService.showInstallPrompt();
        if (installed) {
            this.showPrompt.set(false);
            localStorage.removeItem('pwa-install-dismissed');
        }
    }

    dismiss(): void {
        this.showPrompt.set(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
}
